import { API_ENDPOINTS } from "@/constants/api";

export interface Pagination {
  total: number;
  page: number;
  limit: number;
  pages: number;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: Pagination;
}

export interface BaseGeneration {
  id: string;
  createdAt: string;
  updatedAt: string;
  status: "PENDING" | "PROCESSING" | "COMPLETED" | "FAILED";
  errorMessage?: string;
}

export interface SwapGeneration extends BaseGeneration {
  inputImageUrls: string[];
  outputUrls: string[];
  label?: string;
  customPrompt?: string;
}

export interface RoomVisualizationProduct {
  index: number;
  productId: string;
  variantId: string | null;
  sku: string | null;
  name: string;
  imageUrl: string;
  url: string | null;
  category: string;
  price: { amount: number; currency: string } | null;
  dimensions: {
    widthCm: number | null;
    heightCm: number | null;
    lengthCm: number | null;
  } | null;
  box: { x1: number; y1: number; x2: number; y2: number } | null;
}

export interface RoomVisualization {
  id: string;
  status: "queued" | "processing" | "completed" | "failed";
  step: string | null;
  roomType: string | null;
  styles: string[];
  roomImageUrl: string | null;
  preparedRoomImageUrl: string | null;
  resultImageUrl: string | null;
  products: RoomVisualizationProduct[];
  selection: { rationale: string; warnings: string[] } | null;
  bestEffort: boolean;
  error: { code: string; message: string } | null;
  createdAt: string;
  updatedAt: string;
}

export interface TwoDToThreeDGeneration extends BaseGeneration {
  inputImageUrls: string[];
  outputUrls: string[];
  customPrompt?: string;
}

export interface MagicBackgroundGeneration extends BaseGeneration {
  inputImageUrls: string[];
  outputUrls: string[];
}

export interface PropertyVisualizeGeneration extends BaseGeneration {
  type: "IMAGE" | "VIDEO";
  preset?: string;
  inputImageUrls: string[];
  outputUrls: string[];
  startImageUrl?: string;
  endImageUrl?: string;
}

const fetchWithAuth = async <T>(url: string): Promise<T> => {
  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.json();
};

export const getSwaps = async (
  page: number = 1,
  limit: number = 10
): Promise<PaginatedResponse<SwapGeneration>> => {
  return fetchWithAuth<PaginatedResponse<SwapGeneration>>(
    API_ENDPOINTS.GENERATION.SWAP(page, limit)
  );
};

export const getRoomVisualizations = async (
  page: number = 1,
  limit: number = 10
): Promise<PaginatedResponse<RoomVisualization>> => {
  return fetchWithAuth<PaginatedResponse<RoomVisualization>>(
    API_ENDPOINTS.GENERATION.ROOM_VISUALIZE(page, limit)
  );
};

export const get2dTo3d = async (
  page: number = 1,
  limit: number = 10
): Promise<PaginatedResponse<TwoDToThreeDGeneration>> => {
  return fetchWithAuth<PaginatedResponse<TwoDToThreeDGeneration>>(
    API_ENDPOINTS.GENERATION.TWO_D_TO_THREE_D(page, limit)
  );
};

export const getMagicBackgrounds = async (
  page: number = 1,
  limit: number = 10
): Promise<PaginatedResponse<MagicBackgroundGeneration>> => {
  return fetchWithAuth<PaginatedResponse<MagicBackgroundGeneration>>(
    API_ENDPOINTS.GENERATION.MAGIC_BACKGROUND(page, limit)
  );
};

export const getPropertyVisualizations = async (
  page: number = 1,
  limit: number = 10,
  type?: "IMAGE" | "VIDEO"
): Promise<PaginatedResponse<PropertyVisualizeGeneration>> => {
  return fetchWithAuth<PaginatedResponse<PropertyVisualizeGeneration>>(
    API_ENDPOINTS.GENERATION.PROPERTY_VISUALIZE(page, limit, type)
  );
};
